import { Badge, ScrollArea, Skeleton } from '@mantine/core';
import { IconArrowUpRight } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardBlogArticle } from '@xver/libs/strapi/articles';
import { NewTabLink } from '@xver/components/Atoms';

interface BlogArticlesProps {
    isLoading?: boolean;
    articles?: DashboardBlogArticle[];
}

const BLOG_SKELETON_ITEMS = Array.from({ length: 2 });

const BlogArticleSkeleton = memo(function BlogArticleSkeleton() {
    return (
        <div className="w-full h-48 flex flex-col justify-start items-start gap-3 border-[0.5px] bg-surface-2 border-line rounded-md p-4">
            <Skeleton height={12} width="35%" />
            <Skeleton height={20} width="85%" />
            <Skeleton height={16} width="100%" />
            <Skeleton height={16} width="92%" />
            <div className="mt-auto flex items-center gap-2">
                <Skeleton height={24} width={72} radius="xl" />
                <Skeleton height={16} width={88} />
            </div>
        </div>
    );
});

const BlogArticleCard = memo(function BlogArticleCard({
    article,
}: {
    article: DashboardBlogArticle;
}) {
    const { t } = useTranslation('dashboard');
    const title = article.title ?? t('blogArticles.untitled');
    const description = article.description ?? t('blogArticles.noDescription');
    const publishedAt = article.publishedAt
        ? dayjs(article.publishedAt).format('MMM D, YYYY')
        : t('blogArticles.draft');

    return (
        <article className="w-full min-h-48 flex flex-col justify-start items-start gap-3 border-[0.5px] bg-surface-2 border-line rounded-md p-4">
            <div className="w-full flex items-center justify-between gap-3">
                <Badge
                    variant="light"
                    color="blue"
                    size="md"
                    radius="sm"
                    className="inline-flex items-center"
                >
                    <span>{t('blogArticles.badge')}</span>
                </Badge>
                <span className="text-xs text-fg/68">{publishedAt}</span>
            </div>
            <h3 className="text-lg font-semibold leading-snug line-clamp-3">{title}</h3>
            <p className="text-sm text-fg/70 line-clamp-2">{description}</p>
            {article.slug ? (
                <NewTabLink
                    href={`https://xver.cloud/th/blog/${article.slug}`}
                    className="mt-auto flex items-center gap-2 text-sm text-fg/68 hover:text-fg/80 transition-colors"
                >
                    <span className="underline-offset-4 hover:underline">/{article.slug}</span>
                    <IconArrowUpRight size={16} aria-hidden="true" />
                </NewTabLink>
            ) : (
                <span className="mt-auto text-sm text-fg/68">{t('blogArticles.fallbackLink')}</span>
            )}
        </article>
    );
});

function BlogArticles({ isLoading = false, articles = [] }: BlogArticlesProps) {
    const { t } = useTranslation('dashboard');

    return (
        <div className="w-full flex flex-col justify-start items-start gap-1 overflow-hidden">
            <h2 className="text-xl">{t('blogArticles.heading')}</h2>
            <p className="text-sm text-fg/68">{t('blogArticles.subtitle')}</p>
            <ScrollArea className="mt-3.5 w-full h-124" scrollbars="y" overscrollBehavior="contain">
                <div className="w-full grid grid-cols-1 gap-4">
                    {isLoading ? (
                        BLOG_SKELETON_ITEMS.map((_, index) => <BlogArticleSkeleton key={index} />)
                    ) : articles.length > 0 ? (
                        articles.map((article) => (
                            <BlogArticleCard key={article.id} article={article} />
                        ))
                    ) : (
                        <div className="w-auto h-48 flex flex-col justify-center items-center gap-2 p-4">
                            <p className="text-sm text-fg/70">{t('blogArticles.noPostsTitle')}</p>
                        </div>
                    )}
                </div>
            </ScrollArea>
        </div>
    );
}

export default memo(BlogArticles);
